import { Command } from 'commander';
import { getV2Client } from '../api/v2-client.js';
import { getActiveOrgSlug } from '../active-org.js';
import { handleV2Error } from '../handle-error.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';

interface WhoamiOpts { json?: boolean; output?: string; }

interface V2UserProfile {
  user_id: string;
  name: string;
  email: string;
  slug: string;
  // Decision 045: dual-emit — prefer `primary_grid_id`, fall back to legacy.
  primary_grid_id?: string | null;
  primary_org_id?: string | null;
}
interface V2OrgSummary {
  // Decision 045: dual-emit — prefer `grid_id`, fall back to legacy `org_id`.
  grid_id?: string;
  org_id?: string;
  slug: string;
  name: string;
  role: string;
  member_count: number;
}
interface V2SpaceSummary {
  space_id: string;
  slug: string;
  name: string;
  is_default?: boolean;
  member_count?: number;
  archived_at?: string | null;
}

export async function runWhoami(opts: WhoamiOpts): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const client = await getV2Client();
  const [me, orgs, spacesResp] = await Promise.all([
    client.get<V2UserProfile>('/api/v2/users/me'),
    client.get<{
      orgs: V2OrgSummary[];
      primary_grid_id?: string | null;
      primary_org_id?: string | null;
    }>('/api/v2/grids'),
    client
      .get<{ spaces: V2SpaceSummary[] }>('/api/v2/spaces')
      .catch(() => ({ spaces: [] as V2SpaceSummary[] })),
  ]);

  const activeSlug = getActiveOrgSlug();
  const active = orgs.orgs.find((o) => o.slug === activeSlug) ?? null;
  const primaryId = orgs.primary_grid_id ?? orgs.primary_org_id ?? null;

  out.json(() => ({
    user: me,
    active_org_slug: activeSlug,
    orgs: orgs.orgs,
    primary_grid_id: primaryId,
    spaces: spacesResp.spaces,
  }));
  out.human(() => {
    const lines: string[] = [];
    lines.push(`${me.name} (${me.email})`);
    lines.push(`Handle: ${me.slug}`);
    if (active) {
      lines.push(`Active grid: ${active.name} (${active.slug}) — ${active.role}`);
    } else {
      lines.push('Active grid: (none set; run `grid login` or pass --grid <slug>)');
    }
    lines.push(`Member of ${orgs.orgs.length} grid${orgs.orgs.length === 1 ? '' : 's'}:`);
    for (const o of orgs.orgs) {
      const mark = o.slug === activeSlug ? '✓' : ' ';
      const primary = (o.grid_id ?? o.org_id) === primaryId ? ' — primary' : '';
      lines.push(`  ${mark} ${o.name} (${o.slug}) — ${o.role}${primary}`);
    }
    if (active && spacesResp.spaces.length > 0) {
      lines.push(`Spaces in ${active.name}:`);
      for (const s of spacesResp.spaces) lines.push(`  - ${s.name} (${s.slug})`);
    }
    return lines.join('\n');
  });
}

export const whoamiCommand = addOutputOption(
  new Command('whoami')
    .description('Show current user and active grid')
    .option('--json', 'JSON output (alias of -o json)'),
).action(async (opts: WhoamiOpts) => {
  try { await runWhoami(opts); }
  catch (err) { await handleV2Error(err, { json: isStructuredOutput(opts) }); }
});
